package nl.volleyplanner.web.controller.authorization

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.browser.document
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.ButtonType
import kotlinx.html.InputType
import kotlinx.html.button
import kotlinx.html.div
import kotlinx.html.dom.create
import kotlinx.html.form
import kotlinx.html.id
import kotlinx.html.input
import kotlinx.html.js.onClickFunction
import kotlinx.html.label
import kotlinx.html.span
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.RootPageController
import nl.volleyplanner.web.base.WebPageController
import nl.volleyplanner.web.base.routing.NavigationCause
import nl.volleyplanner.web.base.routing.NavigationManager
import nl.volleyplanner.web.base.routing.PageRouter
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.launchAsync
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

class LoginController : RootPageController() {
    private val logger = Logger.Companion.getLogger("nl.volleyplanner.web.controller.LoginController")
    override val name = "LoginController"
    override val title = "Login"
    override val path = "/login"
    override val requiredAuthLevel = AuthLevel.AnonymousPublic
    override val childPages: List<WebPageController> = emptyList()

    init {
        CoroutineManager.CONTROLLER_SCOPE.launch {
            AuthContextService.authUserState.collectLatest { authUser ->
                if (authUser != null) {
                    when (authUser.authLevel) {
                        AuthLevel.AdminUser -> {
                            // If we are at the /login page then navigate to /planner
                            // Otherwise, login is shown on an actual page. We can refresh to load the correct controller
                            if (NavigationManager.state.value.state.path == path) {
                                NavigationManager.navigateTo("/planner", NavigationCause.Programmatic)
                            } else {
                                PageRouter.forceReload()
                            }
                        }
                        else -> {
                            PageRouter.forceReload()
                        }
                    }
                } else {
                    PageRouter.forceReload()
                }
            }
        }
    }

    override fun createRuntimeInstance() = LoginController()

    fun login() {
        val loginButton = document.getElementById("login-form-button") as HTMLButtonElement
        val spinnerElement = document.create.span {
            id = "login-button-spinner"
            spinner(small = true)
        }
        loginButton.insertBefore(spinnerElement, loginButton.firstChild)
        loginButton.addClass("btn-secondary")
        loginButton.removeClass("btn-primary")

        val email = (document.querySelector("#emailInput") as HTMLInputElement).value
        val password = (document.querySelector("#passwordInput") as HTMLInputElement).value
        launchAsync {
            Firebase.auth.signInWithEmailAndPassword(email, password)
        }.onComplete { authResult ->
            if (authResult.user == null) {
                (document.querySelector("#login-error-message") as HTMLElement)
                    .innerText = "Login mistlukt"
                loginButton.removeClass("btn-secondary")
                loginButton.addClass("btn-danger")
                loginButton.removeChild(spinnerElement)
            } else {
                logger.debug("Login gelukt, token ophalen")
                launchAsync {
                    Firebase.auth.currentUser!!.getIdTokenResult(false)
                }
            }
        }.onError {
            (document.querySelector("#login-error-message") as HTMLElement)
                .innerText = "Login mistlukt"
            loginButton.removeClass("btn-secondary")
            loginButton.addClass("btn-danger")
            loginButton.removeChild(spinnerElement)
        }
    }

    override suspend fun renderContent(): HTMLElement {
        return document.create.div(classes = "container") {
            form {
                div {
                    id = "login-error-message"
                }
                div(classes = "mb-3") {
                    label(classes = "form-label") {
                        htmlFor = "emailInput"
                        +"Email"
                    }
                    input(classes = "form-control") {
                        id = "emailInput"
                        type = InputType.email
                        autoComplete = "username"
                    }
                }
                div(classes = "mb-3") {
                    label(classes = "form-label") {
                        htmlFor = "passwordInput"
                        +"Wachtwoord"
                    }
                    input(classes = "form-control") {
                        id = "passwordInput"
                        type = InputType.password
                        autoComplete = "current-password"
                    }
                }
                button(classes = "btn btn-primary") {
                    id = "login-form-button"
                    type = ButtonType.submit
                    onClickFunction = { event ->
                        event.preventDefault()

                        login()
                    }

                    +"Login"
                }
            }
        }
    }
}